import { yupResolver } from "@hookform/resolvers/yup";
import { useMobile } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import {
  ApplicationForm,
  FORM_STEP,
  useApplicationFormNavigation,
} from "@krea/customer-features/shared-components/application-form";
import { useApplicationFlowForm } from "@krea/customer-features/shared-components/application-form/hooks/useApplicationFlowForm";
import { getDefaultValues } from "@krea/customer-features/shared-components/application-form/utils";
import CheckBox from "@krea/customer-features/shared-components/fields/checkbox";
import { Label } from "@krea/customer-features/shared-components/fields/label";
import LoanApplicationFormLayout from "@krea/customer-features/shared-components/loan-application-form-layout";
import { Text } from "@krea/customer-features/shared-components/text";
import { useBankIDStore } from "@krea/customer-features/store/bankID/hooks";
import { useLoanApplicationFormStore } from "@krea/customer-features/store/loan-application-form/hooks";
import { getApplicationPayload } from "@krea/customer-features/store/loan-application-form/utils";
import { useModalStore } from "@krea/customer-features/store/modal/hooks";
import { useGetUserData } from "@krea/customer-features/store/user/hooks/queries";
import {
  COUNTRY_CODE,
  termsOfServiceLink,
} from "@krea/customer-features/utils";
import classnames from "classnames";
import { useCallback, useEffect } from "react";
import { useController, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useShallow } from "zustand/react/shallow";

import { IdentifyCustomer } from "@/components/modals/remote-identification-modals/identify-customer";

import { useInitiateLoanApplication } from "store/loan-application-form/hooks";

import { ApplicationSummary } from "../../components/application-form/application-summary";
import { CampaignInput } from "../../components/application-form/fields/campaign";
import { IdentificationButtonBankId } from "../../components/application-form/fields/identification-button-bankid";
import { IdentificationButtonSMS } from "../../components/application-form/fields/identification-button-sms";
import { RemoteIdentificationModalBankID } from "../../components/modals/remote-identification-modals";

enum LoanApplicationNotificationReceiver {
  CONTACT_PERSON = "CONTACT_PERSON",
  PARTNER = "PARTNER",
  CONTACT_PERSON_AND_PARTNER = "CONTACT_PERSON_AND_PARTNER",
}

enum ERemoteIdentificationType {
  SMS = "SMS",
  BANK_ID = "BANK_ID",
}

interface IRemoteIdentificationType {
  type: ERemoteIdentificationType;
  URL: string;
  URLExpiryDate: string;
  isCustomerApplicationFlowAllowed: boolean;
}

interface IOrganisation {
  name: string;
}

interface IState {
  organisation: IOrganisation;
  remoteIdentification: IRemoteIdentificationType;
  utmCampaign: string;
  setUtmCampaign: (utmCampaign: string) => void;
  setNotificationReceiver: (
    notificationReceiver: LoanApplicationNotificationReceiver | null,
  ) => void;
  notificationReceiver: LoanApplicationNotificationReceiver | null;
  setRemoteIdentification: (
    type: Record<string, ERemoteIdentificationType>,
  ) => void;
  currentStep: string;
  setCurrentStep: (currentStep: string) => void;
  remoteIdentificationType: ERemoteIdentificationType;
  resetFormState: () => void;
  resetBankIDStore: () => void;
}

const PartnerApplicationFields = ({
  onSubmit,
  onBack,
}: {
  onSubmit: (remoteIdentificationType: ERemoteIdentificationType) => void;
  onBack: () => void;
}) => {
  const { t } = useTranslation();
  const validationSchema = Yup.object().shape({
    utmCampaign: Yup.string().nullable(),
    notificationReceiver: Yup.mixed<LoanApplicationNotificationReceiver>()
      .required("Notification Receiver is required")
      .oneOf(Object.values(LoanApplicationNotificationReceiver)),
  });

  const {
    organisation,
    remoteIdentification,
    utmCampaign,
    notificationReceiver,
    setNotificationReceiver,
    setUtmCampaign,
  } = useLoanApplicationFormStore(
    useShallow((state) => ({
      organisation: state.organisation,
      remoteIdentification: state.remoteIdentification,
      utmCampaign: state.utmCampaign,
      notificationReceiver: state.notificationReceiver,
      setUtmCampaign: state.setUtmCampaign,
      setNotificationReceiver: state.setNotificationReceiver,
    })),
  );

  const { control, handleSubmit, formState } = useForm({
    mode: "onChange",
    defaultValues: {
      utmCampaign: utmCampaign,
      notificationReceiver:
        (notificationReceiver as
          | LoanApplicationNotificationReceiver
          | undefined) ?? LoanApplicationNotificationReceiver.CONTACT_PERSON,
    },
    resolver: yupResolver(validationSchema),
  });

  const utmCampaignController = useController({
    name: "utmCampaign",
    control,
  });

  const notificationReceiverController = useController({
    name: "notificationReceiver",
    control,
  });

  const onUtmCampaignChange = (value: string) => {
    utmCampaignController.field.onChange(value);
    setUtmCampaign(value);
  };

  const notificationReceiverError = formState.errors.notificationReceiver;

  const onNotificationReceiverChange = (name: string) => {
    let newNotificationReceiver: LoanApplicationNotificationReceiver | null =
      null;

    // const name = event.target.name;

    switch (notificationReceiver) {
      case LoanApplicationNotificationReceiver.CONTACT_PERSON_AND_PARTNER:
        if (name === "customer") {
          newNotificationReceiver = LoanApplicationNotificationReceiver.PARTNER;
        } else if (name === "partner") {
          newNotificationReceiver =
            LoanApplicationNotificationReceiver.CONTACT_PERSON;
        }
        break;
      case LoanApplicationNotificationReceiver.CONTACT_PERSON:
        if (name === "partner") {
          newNotificationReceiver =
            LoanApplicationNotificationReceiver.CONTACT_PERSON_AND_PARTNER;
        }
        break;
      case LoanApplicationNotificationReceiver.PARTNER:
        if (name === "customer") {
          newNotificationReceiver =
            LoanApplicationNotificationReceiver.CONTACT_PERSON_AND_PARTNER;
        }
        break;
      case null:
        if (name === "customer") {
          newNotificationReceiver =
            LoanApplicationNotificationReceiver.CONTACT_PERSON;
        } else if (name === "partner") {
          newNotificationReceiver = LoanApplicationNotificationReceiver.PARTNER;
        }
        break;
    }
    if (!newNotificationReceiver) return;
    notificationReceiverController.field.onChange(newNotificationReceiver);
    setNotificationReceiver(newNotificationReceiver);
  };

  const onMethodSelect = (
    remoteIdentificationType: ERemoteIdentificationType,
  ) => {
    handleSubmit(
      (_data) => {
        onSubmit(remoteIdentificationType);
      },
      (errors) => {
        console.warn("invalid", errors);
      },
    )();
  };

  const companyName = organisation.name || "";

  const selectedCheckboxStyle =
    "tw-border-border tw-border tw-p-3 tw-bg-gray tw-rounded";
  const unselectedCheckboxStyle =
    "tw-border-border tw-border tw-p-3 tw-rounded";

  useEffect(() => {
    if (!notificationReceiver) onNotificationReceiverChange("customer");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationReceiver]);

  const { data: user } = useGetUserData();
  const customer = getApplicationPayload();

  const { isPending } = useInitiateLoanApplication();

  const partnerChecked =
    notificationReceiver === LoanApplicationNotificationReceiver.PARTNER ||
    notificationReceiver ===
      LoanApplicationNotificationReceiver.CONTACT_PERSON_AND_PARTNER;

  const customerChecked =
    notificationReceiver ===
      LoanApplicationNotificationReceiver.CONTACT_PERSON ||
    notificationReceiver ===
      LoanApplicationNotificationReceiver.CONTACT_PERSON_AND_PARTNER;

  return (
    <LoanApplicationFormLayout
      heading={t("applicationForm.identificationHeading")}
      subheading={t("applicationForm.weAreObligedToIdentifyYou", {
        companyName,
      })}
      upperInfoText={
        settings.countryCode === COUNTRY_CODE.SE
          ? t("applicationForm.common.noApplicationOrCreditCheck")
          : t("applicationForm.common.noApplication")
      }
      innerForm={
        <div className="my-7">
          <CampaignInput
            name="utmCampaign"
            value={utmCampaignController.field.value ?? ""}
            onChange={(e) => onUtmCampaignChange(e.target.value)}
            errors={
              utmCampaignController.fieldState.isTouched &&
              utmCampaignController.fieldState.error?.message
                ? utmCampaignController.fieldState.error.message
                : ""
            }
          />
          <div className="tw-mt-5 tw-flex-col">
            <Label>{t("applicationForm.whoShouldReceiveNotifications")}</Label>
            <div className="w-4/12 tw-rounded-lg tw-border-primary">
              <div className="tw-mb-2">
                <div
                  className={
                    customerChecked
                      ? selectedCheckboxStyle
                      : unselectedCheckboxStyle
                  }
                >
                  <CheckBox
                    className="tw-mr-3"
                    id="customer"
                    label={t("applicationForm.common.customer")}
                    onChange={(e) =>
                      onNotificationReceiverChange(e.target.name)
                    }
                    error={notificationReceiverError?.message}
                    name="customer"
                    checked={customerChecked}
                  />
                </div>
              </div>
              <div
                className={
                  partnerChecked
                    ? selectedCheckboxStyle
                    : unselectedCheckboxStyle
                }
              >
                <CheckBox
                  className="tw-mr-3"
                  id="partner"
                  label={t("applicationForm.common.partner")}
                  onChange={(e) => onNotificationReceiverChange(e.target.name)}
                  error={notificationReceiverError?.message}
                  name="partner"
                  checked={partnerChecked}
                />
              </div>
            </div>
          </div>
          <div className="mt-7">
            {settings.countryCode === COUNTRY_CODE.SE && (
              <>
                <IdentificationButtonBankId
                  variant="contained"
                  disabled={isPending || !notificationReceiver}
                  onClick={() =>
                    onMethodSelect(ERemoteIdentificationType.BANK_ID)
                  }
                />
                <div
                  className={classnames(
                    "d-flex justify-content-center shrink-0 order-0 mb-5 mt-7 text-center border-top",
                  )}
                >
                  <Text
                    size="sm"
                    className="bg-white font-weight-bold px-2 tw-mt-[-12px]"
                  >
                    {t("applicationForm.commons.formOr")}
                  </Text>
                </div>
              </>
            )}
            <IdentificationButtonSMS
              variant={
                settings.countryCode === COUNTRY_CODE.SE
                  ? "outlined"
                  : "contained"
              }
              disabled={
                formState.isSubmitting || isPending || !notificationReceiver
              }
              onClick={() => onMethodSelect(ERemoteIdentificationType.SMS)}
            />

            <div className="order-1 d-flex justify-content-center w-100">
              <div className="d-flex align-items-start justify-content-center mt-4">
                <div>
                  <Text size="sm" className="font-weight-bold text-center">
                    {t("applicationForm.commons.subHeading")}
                  </Text>

                  <Text size="sm" className="text-center font-weight-bold pr-1">
                    <Trans i18nKey="applicationForm.thirdPartyTosText">
                      Placeholder: Genom att identifiera dig godkänner du&nbsp;
                      <a
                        target="_blank"
                        href={termsOfServiceLink(
                          "",
                          settings.countryWebsiteUrl,
                          "",
                        )}
                        rel="noopener noreferrer"
                      >
                        Placeholder: användarvillkoren
                      </a>
                    </Trans>
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      hideNavigation={false}
      hideAdvisorCTABanner
      warningInfoTextIndex={1}
      goToPreviousStep={onBack}
      goToNextStep={() => {}}
    />
  );
};

export const ApplicationFormPage = () => {
  const isMobile = useMobile();

  // const queryClient = useQueryClient();
  const { getPreviousStep } = useApplicationFormNavigation();

  const openModal = useModalStore(
    ({ openModal }: { openModal: (arg: any) => void }) => openModal,
  );

  const resetBankIDStore = useBankIDStore(
    (state: IState) => state.resetBankIDStore,
  );
  const {
    setCurrentStep,
    currentStep,
    remoteIdentification,
    setRemoteIdentification,
    resetFormState,
  } = useLoanApplicationFormStore((state) => ({
    currentStep: state.currentStep,
    setCurrentStep: state.setCurrentStep,
    remoteIdentification: state.remoteIdentification,
    setRemoteIdentification: state.setRemoteIdentification,
    resetFormState: state.resetFormState,
  }));

  const applicationFormProps = useApplicationFlowForm();

  const onFormReset = () => {
    resetFormState();
    const defaultValues = getDefaultValues();
    applicationFormProps.reset(defaultValues);
  };

  const onBack = () => {
    resetBankIDStore();
    setCurrentStep(getPreviousStep());
  };

  const onSubmit = useCallback(
    (remoteIdentificationType: ERemoteIdentificationType) => {
      // If BankID we need to initiate the call from the BankIDFlow component after filling in the form
      setRemoteIdentification({
        ...remoteIdentification,
        type: remoteIdentificationType as string,
      });

      if (remoteIdentificationType === ERemoteIdentificationType.BANK_ID) {
        openModal({
          modal: <RemoteIdentificationModalBankID />,
          closeOnBackgroundClick: false,
        });
      } else if (
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        remoteIdentificationType === ERemoteIdentificationType.SMS
      ) {
        openModal({
          modal: <IdentifyCustomer />,
          closeOnBackgroundClick: false,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setRemoteIdentification],
  );

  useEffect(() => {
    // Scroll to top on step change on mobile
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  }, [currentStep, isMobile]);

  return (
    <div>
      {currentStep !== FORM_STEP.BASIC_DETAILS && (
        <ApplicationSummary onReset={() => onFormReset()} />
      )}
      {currentStep === FORM_STEP.COMPLETE ? (
        <PartnerApplicationFields onSubmit={onSubmit} onBack={onBack} />
      ) : (
        <ApplicationForm
          applicationFormProps={applicationFormProps}
          onComplete={() => {}}
          shouldCreateLead={false}
        />
      )}
    </div>
  );
};
